import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Image, Input, InputNumber, message, Modal, Space, Typography, Upload } from 'antd';
import { DeleteOutlined, InboxOutlined } from '@ant-design/icons';
import { shipmentApi, type ShipmentDTO, type ShipmentWeightConfirmationDTO } from '../api/kfc';

const MAX_IMAGES = 9;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_TEXT = 2000;
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

type PendingImage = { uid: string; file: File; url: string };

interface Props {
  shipment: ShipmentDTO | null;
  onClose: () => void;
  onConfirmed: (record: ShipmentWeightConfirmationDTO) => void;
}

/** 确认重量：重量必填，凭证文字 / 图片选填；每次确认后端保存一条记录 */
const WeightConfirmModal: React.FC<Props> = ({ shipment, onClose, onConfirmed }) => {
  const [form] = Form.useForm<{ confirmedWeight: number; evidenceText?: string }>();
  const [images, setImages] = useState<PendingImage[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const imagesRef = useRef<PendingImage[]>([]);
  imagesRef.current = images;
  const open = !!shipment;

  const clearImages = useCallback(() => {
    imagesRef.current.forEach((img) => URL.revokeObjectURL(img.url));
    setImages([]);
  }, []);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      clearImages();
    }
  }, [open, form, clearImages]);

  const addFiles = useCallback((files: File[]) => {
    const current = imagesRef.current;
    const accepted: PendingImage[] = [];
    for (const f of files) {
      if (!ALLOWED_TYPES.includes(f.type)) {
        message.warning(`图片格式仅支持 png / jpg / webp：${f.name || '剪贴板图片'}`);
        continue;
      }
      if (f.size > MAX_IMAGE_BYTES) {
        message.warning(`单张图片不能超过 5MB：${f.name || '剪贴板图片'}`);
        continue;
      }
      if (current.length + accepted.length >= MAX_IMAGES) {
        message.warning(`图片最多 ${MAX_IMAGES} 张`);
        break;
      }
      accepted.push({ uid: `${Date.now()}-${Math.random()}`, file: f, url: URL.createObjectURL(f) });
    }
    if (accepted.length) setImages([...current, ...accepted]);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onPaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const target = e.target as HTMLElement | null;
      const inTextField = !!target && (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT');
      // 同时带文字和图片的剪贴板（如 Excel）：在输入框内粘贴时按文字处理
      if (inTextField && Array.from(items).some((it) => it.kind === 'string' && it.type === 'text/plain')) return;
      const files: File[] = [];
      let n = imagesRef.current.length;
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (it.kind === 'file' && it.type.startsWith('image/')) {
          const blob = it.getAsFile();
          if (!blob) continue;
          n += 1;
          const ext = blob.type === 'image/jpeg' ? 'jpg' : blob.type === 'image/webp' ? 'webp' : 'png';
          files.push(new File([blob], blob.name && blob.name !== 'image.png' ? blob.name : `paste-${n}.${ext}`, { type: blob.type }));
        }
      }
      if (files.length) {
        e.preventDefault();
        addFiles(files);
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [open, addFiles]);

  const removeImage = (uid: string) => {
    setImages((prev) => {
      const target = prev.find((p) => p.uid === uid);
      if (target) URL.revokeObjectURL(target.url);
      return prev.filter((p) => p.uid !== uid);
    });
  };

  const onOk = () => {
    if (!shipment) return;
    form.validateFields().then((values) => {
      const fd = new FormData();
      fd.append('confirmedWeight', String(values.confirmedWeight));
      const text = values.evidenceText?.trim();
      if (text) fd.append('evidenceText', text);
      images.forEach((img) => fd.append('images', img.file, img.file.name));
      setSubmitting(true);
      shipmentApi
        .weightConfirmWithEvidence(shipment.shipmentId, fd)
        .then((res) => {
          message.success('重量已确认');
          onConfirmed(res.data);
        })
        .catch((e) => message.error(e.response?.data?.message || '操作失败'))
        .finally(() => setSubmitting(false));
    });
  };

  return (
    <Modal
      title="确认重量"
      open={open}
      zIndex={1100}
      width={560}
      onOk={onOk}
      confirmLoading={submitting}
      onCancel={onClose}
      afterOpenChange={(o) => {
        if (o && shipment) {
          form.setFieldsValue({
            confirmedWeight: shipment.billingCalcSuggestedKg ?? shipment.pendingWeight ?? shipment.confirmedWeight,
          });
        }
      }}
    >
      {shipment && (
        <>
          {!shipment.hasBillingRule && (
            <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="渠道没有计费重计算规则，请手动输入计费重" />
          )}
          {shipment.hasBillingRule && shipment.billingRuleName && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={`规则：${shipment.billingRuleName}`}
              description={
                shipment.billingCalcSuggestedKg != null
                  ? `建议计费重 ${shipment.billingCalcSuggestedKg} kg`
                  : '可先在详情中点击「计算计费重」'
              }
            />
          )}
          <Form form={form} layout="vertical">
            <Form.Item name="confirmedWeight" label="确认代理计费重(kg)" rules={[{ required: true }]}>
              <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="evidenceText" label="确认凭证（选填）">
              <Input.TextArea
                rows={3}
                maxLength={MAX_TEXT}
                showCount
                placeholder="如：代理微信确认 20.5kg、客户邮件同意按 21kg 计费等"
              />
            </Form.Item>
          </Form>
          <div style={{ marginBottom: 6 }}>
            凭证图片
            <Typography.Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
              选填，最多 {MAX_IMAGES} 张，单张 ≤ 5MB，png / jpg / webp；可在对话框内 Ctrl+V 粘贴截图（已选 {images.length} 张）
            </Typography.Text>
          </div>
          <Upload.Dragger
            multiple
            accept=".png,.jpg,.jpeg,.webp"
            showUploadList={false}
            disabled={submitting || images.length >= MAX_IMAGES}
            beforeUpload={(file, fileList) => {
              if (file === fileList[0]) addFiles(fileList as unknown as File[]);
              return false;
            }}
            style={{ padding: '4px 0' }}
          >
            <p className="ant-upload-drag-icon" style={{ marginBottom: 4 }}>
              <InboxOutlined />
            </p>
            <p className="ant-upload-text" style={{ fontSize: 13 }}>点击或拖入图片；截图可直接 Ctrl+V 粘贴</p>
          </Upload.Dragger>
          {images.length > 0 && (
            <Image.PreviewGroup>
              <Space wrap style={{ marginTop: 8 }}>
                {images.map((img) => (
                  <div key={img.uid} style={{ position: 'relative' }}>
                    <Image src={img.url} width={72} height={72} style={{ objectFit: 'cover', borderRadius: 4 }} />
                    {!submitting && (
                      <Button
                        size="small"
                        danger
                        type="primary"
                        shape="circle"
                        icon={<DeleteOutlined />}
                        style={{ position: 'absolute', top: -6, right: -6 }}
                        onClick={() => removeImage(img.uid)}
                      />
                    )}
                  </div>
                ))}
              </Space>
            </Image.PreviewGroup>
          )}
        </>
      )}
    </Modal>
  );
};

export default WeightConfirmModal;
